import type {
    JitsiAnticsEvent,
    JitsiAnticsMetricFields,
} from 'antics/types'

type JitsiAnticsFieldsContext = {
    event?: unknown
    eventName: JitsiAnticsEvent
    now?: number
    room?: string | null
    startedAt?: number | null
}

type JitsiRTCEndpointRole = 'agent' | 'customer'

export const MAX_RTC_STATS_BYTES = 64 * 1024

const isRecord = (value: unknown): value is Record<string, unknown> => !!value && typeof value === 'object' && !Array.isArray(value)

const optionalBoolean = (value: unknown): boolean | undefined => (typeof value === 'boolean' ? value : undefined)

const optionalNumberString = (value: unknown): string | undefined => (typeof value === 'number' ? String(value) : undefined)

const optionalString = (value: unknown): string | undefined => {
    if (typeof value !== 'string') {
        return undefined
    }

    const stringValue = value.trim()

    return stringValue || undefined
}

const duration = (startedAt?: number | null, now = Date.now()): number | undefined => {
    if (typeof startedAt !== 'number') {
        return undefined
    }

    const seconds = (now - startedAt) / 1000.0

    return seconds >= 0 ? seconds : undefined
}

const deviceCount = (devices: unknown): number => {
    if (Array.isArray(devices)) {
        return devices.length
    }

    if (isRecord(devices)) {
        return Object.keys(devices).length
    }

    return 0
}

const deviceListSummary = (devices: unknown): string | undefined => {
    if (!isRecord(devices)) {
        return undefined
    }

    return [
        ['audioInput', devices.audioInput],
        ['audioOutput', devices.audioOutput],
        ['videoInput', devices.videoInput],
    ]
        .map(([name, deviceGroup]) => `${name}=${deviceCount(deviceGroup)}`)
        .join(',')
}

export const compact = <T extends Record<string, unknown>>(entry: T): T => {
    Object.keys(entry).forEach((key) => {
        if (entry[key] === undefined || entry[key] === null || entry[key] === '') {
            delete entry[key]
        }
    })

    return entry
}

// eslint-disable-next-line complexity
export const buildJitsiAnticsMetricFields = ({
    event,
    eventName,
    now,
    room,
    startedAt,
}: JitsiAnticsFieldsContext): JitsiAnticsMetricFields => {
    const fields: JitsiAnticsMetricFields = {}
    const eventData = isRecord(event) ? event : {}

    switch (eventName) {
        case 'audioAvailabilityChanged':
        case 'videoAvailabilityChanged':
            fields.av = optionalBoolean(eventData.available)
            break

        case 'browserSupport':
            fields.s = optionalBoolean(eventData.supported)
            break

        case 'cameraError':
        case 'micError':
            fields.a = optionalString(eventData.type)
            fields.fail = true
            fields.m = optionalString(eventData.message)
            break

        case 'conferenceCreatedTimestamp':
            fields.a = 'created'
            fields.m = optionalNumberString(eventData.timestamp)
            break

        case 'deviceListChanged':
            fields.a = 'devices'
            fields.m = deviceListSummary(eventData.devices)
            break

        case 'errorOccurred':
            fields.a = optionalString(eventData.type)
            fields.f = optionalBoolean(eventData.isFatal)
            fields.m = optionalString(eventData.message)
            fields.n = optionalString(eventData.name)
            fields.fail = true
            break

        case 'p2pStatusChanged':
            fields.p = optionalBoolean(eventData.isP2p)
            break

        case 'participantJoined':
        case 'participantLeft':
            fields.n = optionalString(eventData.id)
            break

        case 'participantMuted': {
            const isMuted = optionalBoolean(eventData.isMuted)

            fields.a = optionalString(eventData.mediaType)
            fields.m = isMuted === undefined ? undefined : isMuted ? 'muted' : 'unmuted'
            fields.n = optionalString(eventData.participantId)
            break
        }

        case 'participantRoleChanged':
            fields.ro = optionalString(eventData.role)
            break

        case 'peerConnectionFailure':
            fields.p = optionalBoolean(eventData.isP2P)
            fields.wc = optionalBoolean(eventData.wasConnected)
            fields.fail = true
            break

        case 'videoConferenceJoined':
            fields.j = optionalString(eventData.roomName) ?? optionalString(room)
            fields.t = duration(startedAt, now)
            fields.v = optionalBoolean(eventData.visitor)
            fields.n = optionalString(eventData.id)
            break

        case 'videoConferenceLeft':
            fields.j = optionalString(eventData.roomName) ?? optionalString(room)
            break

        case 'videoQualityChanged':
            fields.a = 'quality'
            fields.m = optionalNumberString(eventData.videoQuality)
            break

        case 'readyToClose':
        default:
    }

    return compact(fields)
}

export const buildJitsiRTCStatsMetricFields = (
    entry: unknown,
    endpointRole: JitsiRTCEndpointRole,
): JitsiAnticsMetricFields | undefined => {
    if (!isRecord(entry)) {
        return undefined
    }
    try {
        const serialized = JSON.stringify(entry)
        if (serialized === undefined) {
            return undefined
        }
        const encoded = new TextEncoder().encode(serialized)
        if (encoded.byteLength > MAX_RTC_STATS_BYTES) {
            return undefined
        }
    } catch {
        return undefined
    }
    return {
        ro: endpointRole,
        rtc_st: entry,
    }
}
